import { useEffect, useState } from 'react';
import { getSignalGroupAndName, signalNamesChanged } from './util';
import { callbackUriValidator, nameValidator } from '../../../../../../services/validators/common';
import { UNITS } from '../../utils/webhookTypes';

const validators = {
  name: nameValidator,
  callbackUri: callbackUriValidator,
  errorCallbackUri: callbackUriValidator,
  units: units =>
    !!units &&
      (units.toLowerCase() === UNITS.IMPERIAL ||
        units.toLowerCase() === UNITS.METRIC),
};

const webhookInitialState = {
  signalNames: [],
  name: '',
  callbackUri: '',
  errorCallbackUri: undefined,
  units: 'metric',
  rules: 'asap',
  autoSubscribe: '',
};

const useWebhookFormState = (signals, webhook = webhookInitialState) => {
  const { signalsByGroup } = signals;
  const updateSelectedSignals = () => {
    // removes any individual required signals if they are included in a
    // previously selected group
    const selectedSignals = new Set(webhook.signalNames);
    Array.from(selectedSignals).forEach((signalKey) => {
      if (signalKey.includes('.*')) {
        const groupName = signalKey.split('.*')[0];
        signalsByGroup[groupName].forEach(({ name }) => {
          const signal = `${groupName}.${name}`;
          if (selectedSignals.has(signal)) {
            selectedSignals.delete(signal);
          }
        });
      }
    });
    return selectedSignals;
  };

  const formattedWebhookValues = () => {
    return ({
      ...webhook,
      signalNames: new Set(webhook.signalNames),
      rules: 'asap',
    });
  };

  const [webhookFormValues, setWebhookFormValues] = useState(formattedWebhookValues);
  const [hasEditedWebhook, setHasEditedWebhook] = useState(false);

  useEffect(() => {
    setWebhookFormValues(prev => ({
      ...prev,
      signalNames: updateSelectedSignals(),
    }));
  }, [webhook.signalNames]);

  const [selectedTemplate, setSelectedTemplate] = useState('');
  // enable next automatically if editing an existing webhook
  const [nextEnabled, setNextEnabled] = useState(!!webhook.id);

  const [erroredFields, setErroredFields] = useState({});

  const checkHasEditedWebhook = ({ key, value }) => {
    const updatedValues = {
      ...webhookFormValues,
      [key]: value,
    };
    const hasEdited = (
      updatedValues.name !== webhook.name
      || updatedValues.callbackUri !== webhook.callbackUri
      || updatedValues.errorCallbackUri !== webhook.errorCallbackUri
      || updatedValues.units !== webhook.units
      || updatedValues.signalNames.size > 0
      || signalNamesChanged(
        updatedValues.signalNames,
        new Set(webhook.signalNames), // signalNames stored as array on webhook
      )
    );

    setHasEditedWebhook(hasEdited);
  };

  const clearErroredField = (name) => {
    setErroredFields((prevErroredFields) => {
      const newErroredFields = { ...prevErroredFields };
      delete newErroredFields[name];
      return newErroredFields;
    });
  };

  const updateWebhookFormValues = ({ key, value }) => {
    if (erroredFields[key] && validators[key] && validators[key](value)) {
      clearErroredField(key);
    }
    setWebhookFormValues(prev => ({
      ...prev,
      [key]: value,
    }));
    checkHasEditedWebhook({ key, value });
  };

  const handleInputBlur = (event, fieldRequired = true) => {
    const { name: inputName, value } = event.target;
    if ((!fieldRequired && !value) || !value) {
      clearErroredField(inputName);
      return;
    }
    if (validators[inputName] && !validators[inputName](value)) {
      setErroredFields(prevErroredFields => ({
        ...prevErroredFields,
        [inputName]: true,
      }));
      setNextEnabled(false);
    } else {
      clearErroredField(inputName);
    }
  };

  const handleSignalClick = (signalKey) => {
    const signalGroupClicked = signalKey.includes('.*');

    const updatedSignals = new Set(webhookFormValues.signalNames);

    if (signalGroupClicked) {
      const groupName = signalKey.split('.*')[0];
      // if the group is already selected, remove it
      if (updatedSignals.has(`${groupName}.*`)) {
        updatedSignals.delete(`${groupName}.*`);
      } else {
        // otherwise add the group and remove any
        // individual signals in the group
        updatedSignals.add(`${groupName}.*`);
        signalsByGroup[groupName].forEach(({ name }) => {
          const signal = `${groupName}.${name}`;
          if (updatedSignals.has(signal)) {
            updatedSignals.delete(signal);
          }
        });
      }
    } else {
      const { signalGroup, signal } = getSignalGroupAndName(signalKey);
      // if the group is already selected, remove it
      // and add all signals in that group individually
      if (updatedSignals.has(`${signalGroup}.*`)) {
        updatedSignals.delete(`${signalGroup}.*`);
        signalsByGroup[signalGroup].forEach(({ name }) => {
          if (name !== signal) {
            updatedSignals.add(`${signalGroup}.${name}`);
          }
        });
      } else if (updatedSignals.has(signalKey)) {
        // else if the signal is already selected, remove it
        updatedSignals.delete(signalKey);
      } else {
        // otherwise add the signal individually
        updatedSignals.add(signalKey);
      }
    }

    updateWebhookFormValues({
      key: 'signalNames', value: updatedSignals,
    });
    setNextEnabled(updatedSignals.size > 0);
  };

  return {
    webhookFormValues,
    updateWebhookFormValues,
    handleInputBlur,
    handleSignalClick,
    erroredFields,
    selectedTemplate,
    setSelectedTemplate,
    nextEnabled,
    setNextEnabled,
    hasEditedWebhook,
  };
};

export default useWebhookFormState;
